/**
 * @file TagEditDialog.tsx
 * @description Окно «Новый тег» / «Переименовать тег» (phase19.31, разбор B:
 * ввод нового тега — окном, а не полем над списком на месте главного
 * действия). На телефоне — во весь экран. Ошибку сервера показываем под полем.
 */

import { useEffect, useState, type FormEvent } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import { catalogTags, type CatalogTag } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";

interface Props {
  open: boolean;
  /** Тег на правку; `null` — новый. */
  tag: CatalogTag | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TagEditDialog({ open, tag, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(tag?.displayName ?? "");
    setError(null);
  }, [open, tag]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const displayName = name.trim();
    if (!displayName) return setError("Введите название тега");
    setSaving(true);
    try {
      if (tag) await catalogTags.update(tag.id, { displayName });
      else await catalogTags.create({ displayName });
      notify.success(tag ? "Тег переименован" : "Тег создан");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить тег");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="max-w-md">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{tag ? "Переименовать тег" : "Новый тег"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tag-name">Название</Label>
            <Input
              id="tag-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: без кофеина"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "tag-name-error" : undefined}
              autoFocus
            />
            {error ? (
              <p id="tag-name-error" className="type-caption text-[var(--destructive-text)]">
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={saving}>
              {tag ? "Сохранить" : "Создать"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
